import React from "react";
import { ArrowRight, Facebook, Instagram, Youtube, Sun, HeartHandshake, Users, MapPin } from "lucide-react";
import { PHOTOS, SOCIALS } from "@/lib/site-config";
import SiteNav from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import usePageMeta from "@/lib/use-page-meta";

const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const SOCIAL_LINKS = [
  { key: "tiktok", label: "TikTok", handle: "@chaplinfamilylifeinspain", href: SOCIALS.tiktok, Icon: TikTokIcon },
  { key: "instagram", label: "Instagram", handle: "@chaplinfamilylifeinspain", href: SOCIALS.instagram, Icon: Instagram },
  { key: "facebook", label: "Facebook", handle: "Carley Chaplin", href: SOCIALS.facebook, Icon: Facebook },
  { key: "youtube", label: "YouTube", handle: "@ChaplinFamilyLifeInSpain", href: SOCIALS.youtube, Icon: Youtube },
];

/* ---------------- HERO ---------------- */
const Hero = () => (
  <section className="relative pt-28 pb-20 lg:pt-40 lg:pb-24 overflow-hidden editorial">
    <div className="absolute -top-32 -right-40 w-[640px] h-[640px] rounded-full bg-[var(--gold)]/15 blur-3xl pointer-events-none" />
    <div className="absolute top-1/3 -left-32 w-[420px] h-[420px] rounded-full bg-[var(--terracotta)]/10 blur-3xl pointer-events-none" />

    <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-7">
        <div className="fade-up inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--cream-deep)] border border-[var(--terracotta)]/20 text-xs uppercase tracking-[0.2em] text-[var(--terracotta-deep)] font-medium" data-testid="about-hero-badge">
          <Sun className="w-3.5 h-3.5" /> Meet your guide
        </div>
        <h1 className="fade-up delay-100 font-serif text-[2.6rem] sm:text-[3.4rem] lg:text-[4.2rem] text-[var(--navy)] mt-6 leading-[1.02]" data-testid="about-hero-headline">
          Hi, I'm <em className="text-[var(--terracotta)]">Carley.</em>
          <br />
          <span className="underline-swoosh">We've made this move too.</span>
        </h1>
        <p className="fade-up delay-200 mt-7 text-lg lg:text-xl text-[var(--ink-soft)] max-w-xl leading-relaxed" data-testid="about-hero-subhead">
          In 2023, I moved my entire family — and everything we owned — from the UK to Spain.
          The school runs, the shipping, the paperwork, the appointments, the "what do we do
          first?" moments. We've lived every step of the journey you're about to take.
        </p>
        <div className="fade-up delay-300 mt-10 flex flex-wrap gap-4">
          <a
            href="/#programmes"
            className="pill inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[var(--terracotta)] text-[var(--cream)] font-medium"
            data-testid="about-hero-cta"
          >
            Explore the programmes <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </div>

      <div className="lg:col-span-5 relative fade-in delay-300">
        <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden shadow-2xl">
          <img src={PHOTOS.family_yacht} alt="Carley and her family in Spain" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[var(--navy)]/35 via-transparent to-transparent" />
          <div className="absolute top-6 left-6 text-[var(--cream)] flex items-center gap-2 text-xs uppercase tracking-[0.25em]">
            <MapPin className="w-3.5 h-3.5" /> The Chaplins · Spain since 2023
          </div>
        </div>
        <div className="hidden lg:block absolute -bottom-12 -left-16 w-44 h-56 rounded-2xl overflow-hidden shadow-2xl rotate-[-6deg] border-[6px] border-[var(--cream)]">
          <img src={PHOTOS.family_kids_selfie} alt="The Chaplin children in Spain" className="w-full h-full object-cover" />
        </div>
        <span className="tape -top-3 left-12" />
      </div>
    </div>
  </section>
);

/* ---------------- STORY ---------------- */
const Story = () => (
  <section className="py-24 lg:py-32 bg-[var(--navy)] text-[var(--cream)] relative overflow-hidden editorial grain">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-7">
        <div className="text-xs uppercase tracking-[0.35em] text-[var(--gold)] font-medium">Our story</div>
        <h2 className="font-serif text-4xl lg:text-5xl mt-6 leading-[1.08]" data-testid="about-story-headline">
          We packed up our whole life in the UK.{" "}
          <em className="text-[var(--gold)]">And started again in the sun.</em>
        </h2>
        <div className="mt-8 space-y-5 text-[1.05rem] leading-relaxed text-[var(--cream)]/85">
          <p>
            In 2023 we relocated everything — our home, our children, our work, our routines —
            from the UK to Spain. We know what it feels like to stare at a mountain of forms, to
            get three different answers to the same question, and to wonder whether you've missed
            something that will hold everything up.
          </p>
          <p>
            <strong className="text-[var(--cream)]">We have felt the challenges you're experiencing right now.</strong>{" "}
            That's exactly why Get to Spain exists — and why we've built a fantastic team of trusted
            experts to assist you with your move here: sworn translators, apostille services,
            healthcare brokers and cross-border tax and legal specialists, introduced when your move
            genuinely needs them.
          </p>
          <p>
            Since then, I've personally worked with hundreds of people developing their plans for
            life in Spain — retirees, families, couples and everything in between. Every programme
            carries that lived, practical experience.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
          <div className="border-l-2 border-[var(--gold)] pl-4">
            <div className="font-serif text-3xl">2023</div>
            <div className="text-xs uppercase tracking-wider text-[var(--cream)]/70 mt-1">Our family's move</div>
          </div>
          <div className="border-l-2 border-[var(--gold)] pl-4">
            <div className="font-serif text-3xl">100s</div>
            <div className="text-xs uppercase tracking-wider text-[var(--cream)]/70 mt-1">People guided</div>
          </div>
          <div className="border-l-2 border-[var(--gold)] pl-4">
            <div className="font-serif text-3xl">1:1</div>
            <div className="text-xs uppercase tracking-wider text-[var(--cream)]/70 mt-1">Personal access</div>
          </div>
        </div>
      </div>

      <div className="lg:col-span-5 relative">
        <div className="relative aspect-[3/4] rounded-[2rem] overflow-hidden shadow-2xl">
          <img src={PHOTOS.family_palms} alt="The Chaplin family under the palms in Spain" className="w-full h-full object-cover" />
        </div>
        <div className="absolute -bottom-8 -right-4 lg:-right-10 w-40 h-52 rounded-2xl overflow-hidden shadow-xl border-4 border-[var(--cream)] rotate-[5deg]">
          <img src={PHOTOS.family_beach_sunset} alt="Family beach sunset in Spain" className="w-full h-full object-cover" />
        </div>
      </div>
    </div>
  </section>
);

/* ---------------- VALUES ---------------- */
const Values = () => {
  const items = [
    {
      icon: HeartHandshake,
      title: "Lived experience, not theory",
      body: "Every plan is shaped by what actually happens on the ground — because we've done the move ourselves and guided hundreds of others through it.",
    },
    {
      icon: Users,
      title: "A team of trusted experts",
      body: "When your move needs regulated or specialist help, you're introduced to people we know and trust — no more blind searches.",
    },
    {
      icon: Sun,
      title: "Calm, clear and personal",
      body: "The goal is a calmer, clearer move. You'll always know what to do next, and you'll always have a real person beside you.",
    },
  ];
  return (
    <section className="py-24 lg:py-28 bg-[var(--cream-deep)]/60 editorial">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <h2 className="font-serif text-4xl lg:text-5xl text-[var(--navy)] leading-[1.05] max-w-3xl" data-testid="about-values-headline">
          What working with Carley <em className="text-[var(--terracotta)]">feels like.</em>
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 mt-14">
          {items.map((it, i) => (
            <div key={i} className="soft-card rounded-2xl p-7 hover:bg-white transition-colors duration-500 group" data-testid={`about-value-${i}`}>
              <div className="w-11 h-11 rounded-xl bg-[var(--terracotta)]/10 text-[var(--terracotta)] flex items-center justify-center mb-5 group-hover:bg-[var(--terracotta)] group-hover:text-[var(--cream)] transition-colors duration-500">
                <it.icon className="w-5 h-5" strokeWidth={2} />
              </div>
              <h3 className="font-serif text-2xl text-[var(--navy)] leading-tight">{it.title}</h3>
              <p className="mt-3 text-[var(--ink-soft)] text-[0.95rem] leading-relaxed">{it.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- SOCIALS ---------------- */
const Socials = () => (
  <section className="py-24 lg:py-28 editorial relative overflow-hidden">
    <div className="absolute -top-20 -left-32 w-[500px] h-[500px] rounded-full bg-[var(--gold)]/10 blur-3xl pointer-events-none" />
    <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
      <div className="text-xs uppercase tracking-[0.3em] text-[var(--terracotta)] font-medium">Follow the journey</div>
      <h2 className="font-serif text-4xl lg:text-5xl text-[var(--navy)] mt-5 leading-[1.05]" data-testid="about-socials-headline">
        Chaplin family life <em className="text-[var(--terracotta)]">in Spain.</em>
      </h2>
      <p className="mt-6 text-[var(--ink-soft)] text-lg max-w-2xl mx-auto">
        Real, everyday life after the move — the beaches, the school runs, the fiestas and the
        honest bits in between. Come and say hola.
      </p>
      <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {SOCIAL_LINKS.map((s) => (
          <a
            key={s.key}
            href={s.href}
            target="_blank"
            rel="noreferrer"
            className="group rounded-3xl bg-white border border-black/5 p-7 hover:border-[var(--terracotta)]/40 hover:shadow-xl transition-all flex flex-col items-center gap-3"
            data-testid={`about-social-${s.key}`}
          >
            <span className="w-14 h-14 rounded-full bg-[var(--navy)] text-[var(--cream)] flex items-center justify-center group-hover:bg-[var(--terracotta)] transition-colors">
              <s.Icon className="w-6 h-6" />
            </span>
            <span className="font-serif text-xl text-[var(--navy)]">{s.label}</span>
            <span className="text-xs text-[var(--muted)] break-all">{s.handle}</span>
          </a>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- FINAL CTA ---------------- */
const FinalCTA = () => (
  <section className="py-20 lg:py-24 bg-[var(--cream-deep)] editorial" data-testid="about-final-cta">
    <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
      <h2 className="font-serif text-4xl lg:text-5xl text-[var(--navy)] leading-[1.05]">
        Ready to plan <em className="text-[var(--terracotta)]">your move?</em>
      </h2>
      <p className="mt-6 text-[var(--ink-soft)] text-lg max-w-2xl mx-auto">
        Whether you're applying through the Non-Lucrative Visa or moving with an EU passport,
        there's a programme built around your circumstances.
      </p>
      <div className="mt-9 flex flex-wrap justify-center gap-4">
        <a href="/#programmes" className="pill inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[var(--terracotta)] text-[var(--cream)] font-medium" data-testid="about-cta-programmes">
          Find my route <ArrowRight className="w-4 h-4" />
        </a>
        <a href="/client-success" className="pill inline-flex items-center gap-2 px-8 py-4 rounded-full border border-[var(--navy)]/25 text-[var(--navy)] hover:bg-[var(--navy)] hover:text-[var(--cream)] font-medium" data-testid="about-cta-stories">
          Read client success stories <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  </section>
);

/* ---------------- PAGE ---------------- */
export default function About() {
  usePageMeta(
    "About Carley | Get to Spain",
    "Carley Chaplin moved her whole family from the UK to Spain in 2023. Now she helps others make the move properly — with a trusted team of experts."
  );
  return (
    <main className="min-h-screen bg-[var(--cream)] text-[var(--ink)] selection:bg-[var(--terracotta)] selection:text-[var(--cream)]" data-testid="about-page">
      <SiteNav />
      <Hero />
      <Story />
      <Values />
      <Socials />
      <FinalCTA />
      <SiteFooter />
    </main>
  );
}
